// Thievery operation metadata and constants for SF-27

import type { ThieveryOperation, ThieveryOperationType } from '../types/thievery';

// Operation metadata with costs and descriptions
export const THIEVERY_OPERATIONS: ThieveryOperation[] = [
  // Intel Operations
  {
    type: 'SPY_ON_THRONE',
    name: 'Spy on Throne',
    category: 'INTEL',
    thiefCostPercent: 0.05,
    stealthMultiplier: 1.2,
    successModifier: 1.0,
    description: "Gather comprehensive intelligence on the target's resources, military, population, economy, and magic.",
  },
  {
    type: 'SPY_ON_MILITARY',
    name: 'Spy on Military',
    category: 'INTEL',
    thiefCostPercent: 0.05,
    stealthMultiplier: 1.2,
    successModifier: 1.0,
    description: "Spy on the target's military to learn about troops at home, in training, and out on operations.",
  },
  {
    type: 'INFILTRATE',
    name: 'Infiltrate',
    category: 'INTEL',
    thiefCostPercent: 0.02,
    stealthMultiplier: 1.0,
    successModifier: 1.0,
    description: "Infiltrate the target's thieves guild to estimate their thief count and TPA.",
  },
  {
    type: 'SPY_ON_SCIENCE',
    name: 'Spy on Sciences',
    category: 'INTEL',
    thiefCostPercent: 0.05,
    stealthMultiplier: 1.2,
    successModifier: 1.0,
    description: "Gather intelligence on the target's research progress and science bonuses.",
  },
  {
    type: 'SURVEY',
    name: 'Survey',
    category: 'INTEL',
    thiefCostPercent: 0.02,
    stealthMultiplier: 1.0,
    successModifier: 1.0,
    description: "Survey the target's buildings and land distribution.",
  },
  {
    type: 'SPY_ON_HOME_DEFENSE',
    name: 'Spy on Home Defense',
    category: 'INTEL',
    thiefCostPercent: 0.05,
    stealthMultiplier: 1.2,
    successModifier: 1.0,
    description:
      "Returns the target's exact total home defense including all modifiers (buildings, science, spells, stances, honour, dragon, war). Use this before attacking instead of running four separate intel ops.",
  },
  // Resource Operations
  {
    type: 'ROB_THE_VAULTS',
    name: 'Rob the Vaults',
    category: 'RESOURCE',
    thiefCostPercent: 0.10,
    stealthMultiplier: 1.5,
    successModifier: 1.0,
    description: "Steal gold from the target's treasury.",
  },
  {
    type: 'ROB_THE_GRANARIES',
    name: 'Rob the Granaries',
    category: 'RESOURCE',
    thiefCostPercent: 0.10,
    stealthMultiplier: 1.5,
    successModifier: 1.0,
    description: "Steal food from the target's granaries.",
  },
  {
    type: 'ROB_THE_TOWERS',
    name: 'Rob the Towers',
    category: 'RESOURCE',
    thiefCostPercent: 0.10,
    stealthMultiplier: 1.5,
    successModifier: 1.0,
    description: "Steal runes from the target's towers.",
  },
  {
    type: 'KIDNAPPING',
    name: 'Kidnapping',
    category: 'RESOURCE',
    thiefCostPercent: 0.15,
    stealthMultiplier: 2.0,
    successModifier: 0.8,
    description: 'Capture peasants from the target and add them to your province.',
  },
  {
    type: 'STEAL_HORSES',
    name: 'Steal Horses',
    category: 'RESOURCE',
    thiefCostPercent: 0.10,
    stealthMultiplier: 1.5,
    successModifier: 1.0,
    description: "Steal war horses from the target's stables.",
  },
  {
    type: 'FREE_PRISONERS',
    name: 'Free Prisoners',
    category: 'RESOURCE',
    thiefCostPercent: 0.10,
    stealthMultiplier: 1.5,
    successModifier: 1.0,
    description: "Free prisoners held by the target.",
  },
  // Destructive Operations
  {
    type: 'ARSON',
    name: 'Arson',
    category: 'DESTRUCTIVE',
    thiefCostPercent: 0.15,
    stealthMultiplier: 2.0,
    successModifier: 0.8,
    description: "Burn down enemy buildings to disrupt their infrastructure.",
  },
  {
    type: 'NIGHT_STRIKE',
    name: 'Night Strike',
    category: 'DESTRUCTIVE',
    thiefCostPercent: 0.15,
    stealthMultiplier: 2.0,
    successModifier: 0.8,
    description: 'Assassinate enemy soldiers and specialists during the night.',
  },
  {
    type: 'SABOTAGE_WIZARDS',
    name: 'Sabotage Wizards',
    category: 'DESTRUCTIVE',
    thiefCostPercent: 0.15,
    stealthMultiplier: 2.0,
    successModifier: 0.8,
    description: "Sabotage the target's wizards to reduce their mana rating.",
  },
  // Duration Operations
  {
    type: 'INCITE_RIOTS',
    name: 'Incite Riots',
    category: 'DURATION',
    thiefCostPercent: 0.15,
    stealthMultiplier: 1.8,
    successModifier: 0.85,
    description: "Stir up civil unrest to reduce the target's income.",
  },
  {
    type: 'BRIBE_THIEVES',
    name: 'Bribe Thieves',
    category: 'DURATION',
    thiefCostPercent: 0.15,
    stealthMultiplier: 1.8,
    successModifier: 0.85,
    description: "Bribe the target's thieves to reduce their TPA.",
  },
  {
    type: 'BRIBE_GENERALS',
    name: 'Bribe Generals',
    category: 'DURATION',
    thiefCostPercent: 0.15,
    stealthMultiplier: 1.8,
    successModifier: 0.85,
    description: 'Bribe enemy generals to cause occasional extra losses in combat.',
  },
];

// Stealth cost constants from SF-9A
const STEALTH_COST_PERCENTAGE = 0.05; // 5% of current stealth
const STEALTH_COST_BASE = 3.0; // 3% cap for base operations

// Helper function to get operation metadata by type
export function getOperationByType(type: ThieveryOperationType): ThieveryOperation {
  const operation = THIEVERY_OPERATIONS.find((op) => op.type === type);
  if (!operation) {
    throw new Error(`Unknown operation type: ${type}`);
  }
  return operation;
}

// Calculate thieves to send for an intel operation (floor matches ThieveryMath.resolveDamageTier)
export function calculateThievesToSend(
  totalThieves: number,
  operationType: ThieveryOperationType
): number {
  if (totalThieves === 0) return 0;
  const operation = getOperationByType(operationType);
  return Math.max(1, Math.floor(totalThieves * operation.thiefCostPercent));
}

// Estimate stealth cost for an operation
export function estimateStealthCost(
  currentStealth: number,
  operationType: ThieveryOperationType
): number {
  const operation = getOperationByType(operationType);
  const baseCost = Math.min(currentStealth * STEALTH_COST_PERCENTAGE, STEALTH_COST_BASE);
  return baseCost * operation.stealthMultiplier;
}

// Estimate success rate (simplified client-side calculation)
// Assumes target has similar TPA to player (unknown until operation executes)
export function estimateSuccessRate(playerTPA: number, operationType: ThieveryOperationType): number {
  const operation = getOperationByType(operationType);
  // Use player's own TPA as estimate for target (most provinces have similar TPA)
  const targetEstimatedTPA = 1.0;
  if (targetEstimatedTPA <= 0) return 5;
  const tpaRatio = playerTPA / targetEstimatedTPA;
  const baseSuccess = 0.5 * tpaRatio;
  const finalSuccess = baseSuccess * operation.successModifier;
  return Math.round(Math.max(5, Math.min(95, finalSuccess * 100)));
}

// SF-149: named type (was an inline return-type literal) so ThieveryShellProps can reference it
// by name, matching the ExploreShellProps/SciencesShellProps precedent.
export interface TpaStrength {
  color: string;
  label: string;
}

// Get TPA strength indicator
export function getTPAStrength(tpa: number): TpaStrength {
  if (tpa < 0.5) {
    return { color: 'text-red-400', label: 'Weak' };
  } else if (tpa <= 1.5) {
    return { color: 'text-yellow-400', label: 'Average' };
  } else {
    return { color: 'text-green-400', label: 'Strong' };
  }
}
